import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash, MagnifyingGlass, FloppyDisk, Wrench, Hammer } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TVA_OPTIONS = [0, 5, 5.5, 10, 19, 20, 21];
const UNIT_OPTIONS = ['m²', 'm³', 'ml', 'km', 'u', 'Ens', 'Ft', 'h', '-'];

const fmtEUR = (n) => `${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('fr-FR')} EUR`;

/**
 * Parse a user-entered decimal accepting both `.` and `,` as separator.
 * Returns NaN if the string is not a number. Empty string → 0.
 */
const parseDecimal = (raw) => {
  if (raw == null) return 0;
  const s = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  if (s === '' || s === '-' || s === '.') return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Format a number for display in a text input. Uses comma as decimal
 * separator (FR locale) with up to `decimals` digits. Strips trailing zeros.
 */
const fmtDecimalInput = (n, decimals = 3) => {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '';
  const fixed = Number(n).toFixed(decimals);
  // strip trailing zeros but only after a decimal point — otherwise "90" becomes "9"
  if (!fixed.includes('.')) return fixed;
  return fixed.replace(/\.?0+$/, '').replace('.', ',');
};

/**
 * Decimal input that accepts both `.` and `,` separators and up to N decimals.
 * Keeps a local string buffer so the user can type "1," without it being
 * reformatted mid-typing. Emits the numeric value to onChangeValue on every
 * valid keystroke and reformats on blur.
 */
/**
 * Tiny pill row of preset values. Highlights the active one if it equals
 * the current numeric value. Compact mode = smaller padding/font.
 */
const PresetButtons = ({ values, current, onPick, suffix = '', testIdPrefix = 'preset', compact = false }) => {
  const eq = (a, b) => Math.abs(Number(a || 0) - Number(b || 0)) < 1e-6;
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
      {values.map((v) => {
        const active = eq(v, current);
        return (
          <button key={v} type="button"
            data-testid={`${testIdPrefix}-${v}`}
            onClick={() => onPick(v)}
            style={{
              padding: compact ? '1px 6px' : '3px 8px',
              fontSize: compact ? '0.68rem' : '0.72rem',
              border: '1px solid ' + (active ? '#002FA7' : '#D1D5DB'),
              borderRadius: 999,
              background: active ? '#EEF2FF' : 'white',
              color: active ? '#002FA7' : '#5E5F66',
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              lineHeight: 1.2,
            }}
          >{v}{suffix}</button>
        );
      })}
    </div>
  );
};

const DecimalInput = ({ value, onChangeValue, decimals = 3, dataTestId, className = 'form-input', style, ...rest }) => {
  const [raw, setRaw] = useState(() => fmtDecimalInput(value, decimals));
  // Keep the local string in sync when the parent re-renders with a new value
  // (e.g. after bidirectional recomputation) — but only if the local raw
  // doesn't already represent the same numeric value (avoid clobbering typing).
  useEffect(() => {
    if (parseDecimal(raw) !== Number(value || 0)) {
      setRaw(fmtDecimalInput(value, decimals));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals]);

  const handleChange = (e) => {
    let v = e.target.value;
    // Allow only digits, `.` and `,` (one separator max). Strip everything else.
    v = v.replace(/[^\d.,-]/g, '');
    // Keep only the first separator
    const firstSep = v.search(/[.,]/);
    if (firstSep !== -1) {
      v = v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
    }
    // Drop leading zeros (but keep one zero if the next char is a separator).
    v = v.replace(/^0+(?=\d)/, '');
    // Bare leading separator → prepend a 0 ("0,500", ".5" → "0.5")
    v = v.replace(/^([.,])/, '0$1');
    setRaw(v);
    onChangeValue(parseDecimal(v));
  };

  const handleBlur = () => {
    // Normalize display on blur
    setRaw(fmtDecimalInput(parseDecimal(raw), decimals));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      data-testid={dataTestId}
      className={className}
      value={raw}
      onChange={handleChange}
      onBlur={handleBlur}
      style={style}
      {...rest}
    />
  );
};

const MaterialPicker = ({ catalog, onPick }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    if (!catalog) return [];
    const s = (q || '').trim().toLowerCase();
    if (!s) return catalog.slice(0, 10);
    return catalog.filter(m => (m.name || '').toLowerCase().includes(s) || (m.category || '').toLowerCase().includes(s)).slice(0, 10);
  }, [q, catalog]);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <MagnifyingGlass size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none' }} />
        <input
          data-testid="material-picker-input"
          className="form-input"
          placeholder="Rechercher dans la bibliotheque materiaux..."
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          style={{ paddingLeft: 36, fontSize: '0.85rem' }}
        />
      </div>
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          marginTop: 4, background: 'white', border: '1px solid #E2E2E6',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: 240, overflowY: 'auto'
        }}>
          {matches.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => { onPick(m); setQ(''); setOpen(false); }}
              data-testid={`material-pick-${m.id}`}
              style={{
                width: '100%', textAlign: 'left', border: 'none', background: 'white',
                padding: '8px 10px', cursor: 'pointer', fontSize: '0.82rem',
                display: 'flex', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid #F4F5F7',
              }}
            >
              <span style={{ fontWeight: 600 }}>{m.name}</span>
              <span style={{ color: '#5E5F66', fontSize: '0.74rem' }}>
                {m.category ? `${m.category} - ` : ''}{m.price_ht?.toFixed(2)} EUR / {m.unit || 'u'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Article Editor: full composition form for a library article.
 * Composition: matériaux (from /api/materials/catalog) + main d'oeuvre (heures × taux global).
 * Cost price = auto (materials + MO) OR manual.
 * Selling price HT = cost × (1 + margin%/100).
 */
/**
 * Labor input with a unit toggle (heures / minutes). The underlying form
 * value `labor_hours` is always stored in hours (decimal). When the user
 * toggles to minutes, the input shows hours*60; storing back divides by 60.
 */
const LaborInput = ({ form, setForm, hourlyRate, laborCost, fmtEUR }) => {
  const [unit, setUnit] = useState('h');
  const displayValue = unit === 'min' ? (Number(form.labor_hours || 0) * 60) : form.labor_hours;
  const onChange = (n) => {
    const hours = unit === 'min' ? n / 60 : n;
    setForm((f) => ({ ...f, labor_hours: Math.round(hours * 1000) / 1000 }));
  };
  return (
    <div style={{ background: '#FEF3C7', padding: 12, borderRadius: 8, border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <strong style={{ fontSize: '0.9rem' }}>Main d'oeuvre</strong>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <DecimalInput data-testid="article-labor-hours" dataTestId="article-labor-hours"
          value={displayValue} onChangeValue={onChange}
          decimals={unit === 'h' ? 3 : 0}
          style={{ width: 100, textAlign: 'right', fontSize: '0.85rem' }} />
        <div role="tablist" aria-label="Unite main d'oeuvre"
          style={{ display: 'inline-flex', border: '1px solid #92400E', borderRadius: 6, overflow: 'hidden', fontSize: '0.78rem' }}>
          {['h', 'min'].map((u) => (
            <button key={u} type="button"
              data-testid={`labor-unit-${u}`}
              onClick={() => setUnit(u)}
              aria-selected={unit === u}
              style={{
                padding: '4px 10px', border: 'none', cursor: 'pointer',
                background: unit === u ? '#92400E' : 'white',
                color: unit === u ? 'white' : '#92400E',
                fontWeight: unit === u ? 700 : 500,
              }}
            >{u === 'h' ? 'heures' : 'minutes'}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: '0.85rem' }}>x</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <DecimalInput data-testid="article-hourly-rate" dataTestId="article-hourly-rate"
            value={form.labor_hourly_rate ?? hourlyRate}
            onChangeValue={(n) => setForm((f) => ({ ...f, labor_hourly_rate: n }))}
            decimals={2}
            style={{ width: 90, textAlign: 'right', fontSize: '0.85rem' }}
            title="Taux horaire utilise pour cet article (modifiable)"
          />
          <PresetButtons
            values={[35, 40, 45]}
            suffix=" EUR"
            current={form.labor_hourly_rate ?? hourlyRate}
            onPick={(n) => setForm((f) => ({ ...f, labor_hourly_rate: n }))}
            testIdPrefix="rate-preset"
            compact
          />
        </div>
        <span style={{ fontSize: '0.85rem' }}>EUR/h</span>
        {form.labor_hourly_rate != null && Number(form.labor_hourly_rate) !== Number(hourlyRate) && (
          <button type="button"
            data-testid="reset-hourly-rate-btn"
            onClick={() => setForm((f) => ({ ...f, labor_hourly_rate: null }))}
            title={`Revenir au taux global (${hourlyRate} EUR/h)`}
            style={{ padding: '2px 8px', fontSize: '0.72rem', border: '1px solid #92400E', borderRadius: 4, background: 'white', color: '#92400E', cursor: 'pointer' }}
          >Reset</button>
        )}
      </div>
      {/* Number of workers */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: '0.85rem' }}>x</span>
        <button type="button" data-testid="workers-dec"
          onClick={() => setForm((f) => ({ ...f, labor_workers: Math.max(1, (Number(f.labor_workers) || 1) - 1) }))}
          style={{ width: 26, height: 26, border: '1px solid #92400E', borderRadius: 4, background: 'white', color: '#92400E', cursor: 'pointer', fontWeight: 700 }}
        >-</button>
        <input type="number" min="1" step="1"
          data-testid="article-labor-workers"
          className="form-input"
          value={Number(form.labor_workers) || 1}
          onChange={(e) => setForm((f) => ({ ...f, labor_workers: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
          style={{ width: 56, textAlign: 'center', fontSize: '0.85rem' }}
        />
        <button type="button" data-testid="workers-inc"
          onClick={() => setForm((f) => ({ ...f, labor_workers: (Number(f.labor_workers) || 1) + 1 }))}
          style={{ width: 26, height: 26, border: '1px solid #92400E', borderRadius: 4, background: 'white', color: '#92400E', cursor: 'pointer', fontWeight: 700 }}
        >+</button>
        <span style={{ fontSize: '0.85rem' }}>{Number(form.labor_workers) > 1 ? 'ouvriers' : 'ouvrier'}</span>
      </div>
      <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{fmtEUR(laborCost)}</span>
    </div>
  );
};


export const ArticleEditorModal = ({ show, item = null, onClose, onSaved, onLineFill = null }) => {
  const [categories, setCategories] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [creatingCat, setCreatingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description: '', details: '', supplier: '', category: '', unit: 'u', tva_rate: 20,
    materials: [], labor_hours: 0, labor_hourly_rate: null, labor_workers: 1,
    cost_price_mode: 'auto', manual_cost_price: 0, margin_percent: 30,
  });

  const loadData = useCallback(async () => {
    try {
      const [cRes, mRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoicing/library/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/materials/catalog`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/settings`, { withCredentials: true }),
      ]);
      setCategories(cRes.data || []);
      setCatalog(mRes.data || []);
      setHourlyRate(Number(sRes.data?.labor_hourly_rate) || 35);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!show) return;
    // Load reference lists FIRST, then hydrate the form. This prevents the
    // category/unit Select from rendering an empty trigger when the saved value
    // exists but is not yet present in the options list (race condition).
    let alive = true;
    (async () => {
      await loadData();
      if (!alive) return;
      if (item) {
        setForm({
          description: item.description || '',
          details: item.details || '',
          supplier: item.supplier || '',
          category: item.category || '',
          unit: item.unit || 'u',
          tva_rate: item.tva_rate ?? 20,
          materials: (item.materials || []).map(m => ({ ...m })),
          labor_hours: Number(item.labor_hours) || 0,
          labor_hourly_rate: item.labor_hourly_rate != null ? Number(item.labor_hourly_rate) : null,
          labor_workers: Math.max(1, Number(item.labor_workers) || 1),
          cost_price_mode: item.cost_price_mode || 'auto',
          manual_cost_price: Number(item.manual_cost_price) || 0,
          margin_percent: Number(item.margin_percent ?? 30),
        });
      } else {
        setForm({
          description: '', details: '', supplier: '', category: '', unit: 'u', tva_rate: 20,
          materials: [], labor_hours: 0, labor_hourly_rate: null, labor_workers: 1,
          cost_price_mode: 'auto', manual_cost_price: 0, margin_percent: 30,
        });
      }
    })();
    return () => { alive = false; };
  }, [show, item, loadData]);

  // Computed costs
  const matCost = useMemo(() =>
    form.materials.reduce((s, m) => s + (Number(m.quantity) || 0) * (Number(m.unit_price) || 0), 0),
    [form.materials]);
  // Per-article hourly rate (falls back to global setting if not overridden)
  const effectiveHourlyRate = useMemo(() => {
    const v = form.labor_hourly_rate;
    if (v == null || v === '' || isNaN(Number(v))) return Number(hourlyRate) || 35;
    return Number(v);
  }, [form.labor_hourly_rate, hourlyRate]);
  const laborCost = useMemo(() => {
    const workers = Math.max(1, Number(form.labor_workers) || 1);
    return (Number(form.labor_hours) || 0) * effectiveHourlyRate * workers;
  }, [form.labor_hours, form.labor_workers, effectiveHourlyRate]);
  const autoCost = matCost + laborCost;
  const costPrice = form.cost_price_mode === 'manual' ? Number(form.manual_cost_price || 0) : autoCost;
  const margin = Number(form.margin_percent || 0);
  const sellingPrice = costPrice * (1 + margin / 100);

  const addMaterial = (mat) => {
    const isM2Article = (form.unit || '').trim() === 'm²';
    const coef = Number(mat.coefficient_m2 ?? 1) || 1;
    // When the article is sold per m², default the material quantity to its
    // per-m² coefficient (so user just needs to multiply later by article qty).
    const defaultQty = isM2Article ? coef : 1;
    setForm(f => ({
      ...f,
      materials: [...f.materials, {
        material_id: mat.id, name: mat.name, unit: mat.unit || 'u',
        quantity: defaultQty,
        unit_price: Number(mat.price_ht) || 0,
        coefficient_m2: coef,
      }],
    }));
    if (isM2Article && coef !== 1) {
      toast.success(`Coefficient appliqué : ${coef} ${mat.unit || 'u'}/m²`);
    }
  };
  const updateMaterial = (idx, field, value) => {
    setForm(f => {
      const nm = [...f.materials];
      nm[idx] = { ...nm[idx], [field]: value };
      return { ...f, materials: nm };
    });
  };
  const removeMaterial = (idx) => setForm(f => ({ ...f, materials: f.materials.filter((_, i) => i !== idx) }));

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/library/categories`, { name: newCatName.trim() }, { withCredentials: true });
      setCategories(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(f => ({ ...f, category: res.data.name }));
      setNewCatName('');
      setCreatingCat(false);
      toast.success('Categorie ajoutee');
    } catch (e) { toast.error('Erreur'); }
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.description.trim()) return toast.error('Designation requise');
    setSaving(true);
    try {
      const payload = {
        description: form.description.trim(),
        details: form.details || '',
        supplier: (form.supplier || '').trim(),
        category: form.category || '',
        unit: form.unit || 'u',
        tva_rate: Number(form.tva_rate) || 20,
        unit_price: Math.round(sellingPrice * 100) / 100,
        materials: form.materials.map(m => ({
          material_id: m.material_id || null,
          name: m.name || '',
          unit: m.unit || 'u',
          quantity: Number(m.quantity) || 0,
          unit_price: Number(m.unit_price) || 0,
          coefficient_m2: Number(m.coefficient_m2 ?? 1),
        })),
        labor_hours: Number(form.labor_hours) || 0,
        labor_hourly_rate: form.labor_hourly_rate == null ? null : (Number(form.labor_hourly_rate) || 0),
        labor_workers: Math.max(1, Number(form.labor_workers) || 1),
        cost_price_mode: form.cost_price_mode,
        manual_cost_price: Number(form.manual_cost_price) || 0,
        cost_price_ht: Math.round(costPrice * 100) / 100,
        margin_percent: Number(form.margin_percent) || 0,
      };
      let saved;
      if (item?.id) {
        const res = await axios.put(`${API_URL}/api/invoicing/library/${item.id}`, payload, { withCredentials: true });
        saved = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/invoicing/library`, payload, { withCredentials: true });
        saved = res.data;
      }
      toast.success('Article enregistre');
      if (onLineFill) onLineFill(saved);
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  if (!show) return null;

  return (
    <div data-testid="article-editor-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 12px' }}>
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 880, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wrench size={20} /> {item ? 'Modifier l\'article' : 'Nouvel article'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="article-editor-close"><X size={22} /></button>
        </div>

        <form onSubmit={handleSave} style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Designation + categorie + unite */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px', gap: 10 }}>
            <div>
              <label className="form-label">Designation</label>
              <input data-testid="article-designation" className="form-input" required value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Ex: Pose de placo BA13 sur ossature metallique" />
            </div>
            <div>
              <label className="form-label">Categorie</label>
              {!creatingCat ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Select
                    key={`cat-${item?.id || 'new'}-${categories.length}`}
                    value={form.category || '__none__'}
                    onValueChange={(v) => setForm(f => ({ ...f, category: v === '__none__' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="article-category-select" style={{ flex: 1, fontSize: '0.85rem', height: 38 }}>
                      <SelectValue placeholder="Aucune" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="__none__">Aucune</SelectItem>
                      {/* Inject the saved value if it is not in the loaded
                          categories list yet (race condition + legacy data). */}
                      {form.category && !categories.some(c => c.name === form.category) && (
                        <SelectItem value={form.category}>{form.category}</SelectItem>
                      )}
                      {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreatingCat(true)} title="Nouvelle categorie" data-testid="add-category-btn">
                    <Plus size={14} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input data-testid="new-category-input" className="form-input" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Ex: Charpente" autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); } }} />
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateCategory} data-testid="confirm-new-category">OK</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCreatingCat(false); setNewCatName(''); }}>X</button>
                </div>
              )}
            </div>
            <div>
              <label className="form-label">Unite</label>
              <Select
                key={`unit-${item?.id || 'new'}-${form.unit}`}
                value={form.unit || 'u'} onValueChange={(v) => {
                const wasM2 = (form.unit || '').trim() === 'm²';
                const willBeM2 = (v || '').trim() === 'm²';
                setForm(f => {
                  let newMaterials = f.materials;
                  if (!wasM2 && willBeM2 && f.materials.length > 0) {
                    // Article devient au m² : ré-appliquer le coef à chaque matériau
                    newMaterials = f.materials.map(m => ({
                      ...m,
                      quantity: Number(m.coefficient_m2 ?? 1) || 1,
                    }));
                  }
                  return { ...f, unit: v, materials: newMaterials };
                });
                if (!wasM2 && willBeM2 && form.materials.some(m => Number(m.coefficient_m2 ?? 1) !== 1)) {
                  toast.success('Coefficient /m² appliqué à tous les matériaux');
                }
              }}>
                <SelectTrigger style={{ fontSize: '0.85rem', height: 38 }} data-testid="article-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  {/* Include legacy/custom values (e.g. 'm2', 'forfait') that
                      were imported before the unified unit list, so the Select
                      can render them without falling back to the placeholder. */}
                  {[...new Set([form.unit, ...UNIT_OPTIONS].filter(Boolean))].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description longue + Fournisseur */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div>
              <label className="form-label">Description (optionnel)</label>
              <textarea className="form-input" rows={2} value={form.details}
                onChange={(e) => setForm(f => ({ ...f, details: e.target.value }))}
                placeholder="Description detaillee, conditions, finition..." style={{ resize: 'vertical', fontSize: '0.85rem' }} />
            </div>
            <div>
              <label className="form-label">Fournisseur (optionnel)</label>
              <input
                type="text"
                className="form-input"
                data-testid="article-supplier-input"
                value={form.supplier || ''}
                onChange={(e) => setForm(f => ({ ...f, supplier: e.target.value }))}
                placeholder="Ex: Point P, Saint-Gobain..."
                style={{ fontSize: '0.85rem', height: 38 }}
              />
            </div>
          </div>

          {/* Materiaux */}
          <div style={{ background: '#F8F9FB', padding: 12, borderRadius: 8, border: '1px solid #E2E2E6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Hammer size={16} weight="bold" />
              <strong style={{ fontSize: '0.9rem' }}>Materiaux</strong>
              <span style={{ fontSize: '0.78rem', color: '#5E5F66', marginLeft: 'auto' }}>Total : {fmtEUR(matCost)}</span>
            </div>
            {(form.unit || '').trim() === 'm²' && (
              <div data-testid="article-m2-hint" style={{ marginBottom: 8, padding: '6px 10px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, fontSize: '0.78rem', color: '#1E3A8A', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <span>Article au m² : la quantité de chaque matériau ajouté est pré-remplie avec son coefficient /m².</span>
                {form.materials.length > 0 && (
                  <button
                    type="button"
                    data-testid="reapply-coef-m2-btn"
                    onClick={() => {
                      setForm(f => ({
                        ...f,
                        materials: f.materials.map(m => ({
                          ...m,
                          quantity: Number(m.coefficient_m2 ?? 1) || 1,
                        })),
                      }));
                      toast.success('Coefficient /m² ré-appliqué');
                    }}
                    style={{ padding: '4px 10px', fontSize: '0.75rem', border: '1px solid #1E3A8A', borderRadius: 6, background: 'white', color: '#1E3A8A', cursor: 'pointer', fontWeight: 600 }}
                  >Ré-appliquer le coef</button>
                )}
              </div>
            )}
            <MaterialPicker catalog={catalog} onPick={addMaterial} />
            {form.materials.length > 0 && (
              <table className="data-table" style={{ marginTop: 10, fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th>Materiau</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Qte</th>
                    <th style={{ width: 70 }}>Unite</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Prix HT</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Total HT</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.materials.map((m, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 600 }}>
                        {m.name}
                        {Number(m.coefficient_m2 || 1) !== 1 && (
                          <div style={{ fontSize: '0.7rem', color: '#1E3A8A', marginTop: 2, fontWeight: 500 }}>
                            Coef. {Number(m.coefficient_m2).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} {m.unit}/m²
                          </div>
                        )}
                      </td>
                      <td style={{ padding: 4 }}>
                        <DecimalInput
                          decimals={3}
                          value={m.quantity}
                          onChangeValue={(n) => updateMaterial(idx, 'quantity', n)}
                          dataTestId={`mat-qty-${idx}`}
                          style={{ textAlign: 'right', fontSize: '0.82rem', width: '100%', minWidth: 90 }}
                        />
                      </td>
                      <td style={{ color: '#5E5F66' }}>{m.unit}</td>
                      <td style={{ padding: 4 }}>
                        <DecimalInput
                          decimals={3}
                          value={m.unit_price}
                          onChangeValue={(n) => updateMaterial(idx, 'unit_price', n)}
                          dataTestId={`mat-price-${idx}`}
                          style={{ textAlign: 'right', fontSize: '0.82rem', width: '100%', minWidth: 90 }}
                        />
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtEUR(m.quantity * m.unit_price)}</td>
                      <td>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => removeMaterial(idx)} style={{ padding: 4 }}><Trash size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Main d'oeuvre */}
          <LaborInput form={form} setForm={setForm} hourlyRate={hourlyRate} laborCost={laborCost} fmtEUR={fmtEUR} />

          {/* Prix de revient + marge + prix de vente */}
          <div style={{ background: '#EFF6FF', padding: 12, borderRadius: 8, border: '1px solid #BFDBFE' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <strong style={{ fontSize: '0.9rem' }}>Prix de revient</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem' }}>
                <input type="radio" name="cost-mode" data-testid="cost-mode-auto"
                  checked={form.cost_price_mode === 'auto'}
                  onChange={() => setForm(f => ({ ...f, cost_price_mode: 'auto' }))} />
                Auto (materiaux + MO)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem' }}>
                <input type="radio" name="cost-mode" data-testid="cost-mode-manual"
                  checked={form.cost_price_mode === 'manual'}
                  onChange={() => setForm(f => ({ ...f, cost_price_mode: 'manual' }))} />
                Manuel
              </label>
              {form.cost_price_mode === 'manual' ? (
                <DecimalInput data-testid="manual-cost-input" dataTestId="manual-cost-input"
                  value={form.manual_cost_price}
                  onChangeValue={(n) => setForm(f => ({ ...f, manual_cost_price: n }))}
                  decimals={2}
                  style={{ width: 140, textAlign: 'right', fontSize: '0.85rem' }} />
              ) : (
                <span style={{ fontWeight: 700, color: '#1D4ED8' }}>{fmtEUR(autoCost)}</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Prix de revient HT</label>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1F2024' }}>{fmtEUR(costPrice)}</div>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Marge %</label>
                <DecimalInput data-testid="article-margin" dataTestId="article-margin"
                  value={form.margin_percent}
                  onChangeValue={(n) => setForm(f => ({ ...f, margin_percent: n }))}
                  decimals={3}
                  style={{ textAlign: 'right', fontSize: '0.95rem', fontWeight: 700 }} />
                <PresetButtons
                  values={[20, 30, 40]}
                  suffix="%"
                  current={form.margin_percent}
                  onPick={(n) => setForm(f => ({ ...f, margin_percent: n }))}
                  testIdPrefix="margin-preset"
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Prix de vente HT</label>
                {/* Editable: editing the selling price recomputes the margin% live.
                    If cost is 0, we cannot derive a margin, so we leave the
                    margin untouched and just store the typed price (no-op). */}
                <DecimalInput data-testid="article-selling-price" dataTestId="article-selling-price"
                  value={sellingPrice}
                  onChangeValue={(n) => {
                    if (costPrice > 0) {
                      const m = (n / costPrice - 1) * 100;
                      setForm(f => ({ ...f, margin_percent: Math.max(0, Math.round(m * 1000) / 1000) }));
                    }
                  }}
                  decimals={2}
                  style={{ textAlign: 'right', fontSize: '1.1rem', fontWeight: 800, color: '#047857' }} />
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>TVA</label>
              <Select value={String(form.tva_rate)} onValueChange={(v) => setForm(f => ({ ...f, tva_rate: parseFloat(v) }))}>
                <SelectTrigger style={{ width: 110, fontSize: '0.85rem', height: 32 }}><SelectValue /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  {TVA_OPTIONS.map(r => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" data-testid="article-save" disabled={saving}>
              <FloppyDisk size={16} /> {item ? 'Enregistrer' : 'Creer l\'article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticleEditorModal;
